import {Accordion, AccordionDetails, AccordionSummary, Stack, Typography} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {PropsWithChildren} from 'react';
import DropdownDifficulty, {DropdownDifficultyProps} from '../../atoms/dropdownDifficulty/DropdownDifficulty';
import DropdownModule, {DropdownModuleProps} from '../../atoms/dropdownModule/DropdownModule';
import DropdownQuestionStatus, {
  DropdownQuestionStatusProps,
} from '../../atoms/dropdownQuestionStatus/DropdownQuestionStatus';
import BondDropdownTopicWithFilter, {
  BondDropdownTopicWithFilterProps,
} from '../../bonds/bondDropdownTopicWithFilter/BondDropdownTopicWithFilter';

export interface QuestionFiltersProps {
  dropdownModule: DropdownModuleProps;

  dropdownTopic: BondDropdownTopicWithFilterProps;
  dropdownDifficulty: DropdownDifficultyProps;
  dropdownQuestionStatus: DropdownQuestionStatusProps;

  totalItems?: number;
  expanded: boolean;
  handleExpand: (event: React.SyntheticEvent, isExpanded: boolean) => void;
}

/**
 * Accordion to show filters for questions
 * - only show modules to start with
 * - once a module is selected, show topics, difficulty
 */
export default function QuestionFilters({
  dropdownModule,
  dropdownTopic,
  dropdownDifficulty,
  dropdownQuestionStatus,
  children,
  totalItems,
  expanded,
  handleExpand,
}: PropsWithChildren<QuestionFiltersProps>) {
  return (
    <Accordion expanded={expanded} onChange={handleExpand} sx={{mb: 3}}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{px: 2}}>
        <Typography variant="h6" sx={{color: '#1B263B'}}>
          Filters
          {totalItems !== undefined && (
            <Typography component="span" variant="body2" sx={{ml: 1, color: '#666', fontWeight: 400}}>
              ({totalItems} {totalItems === 1 ? 'question' : 'questions'})
            </Typography>
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2} alignItems="center">
          <DropdownModule
            moduleId={dropdownModule.moduleId}
            onChange={dropdownModule.onChange}
            examModules={dropdownModule.examModules}
            disabled={dropdownModule.disabled}
            label={dropdownModule.label}
            autoSelectFirst={true}
          />

          {dropdownModule.moduleId && (
            <>
              <BondDropdownTopicWithFilter
                value={dropdownTopic.value}
                onChange={dropdownTopic.onChange}
                availableTopics={dropdownTopic.availableTopics}
                allowAll={dropdownTopic.allowAll}
                disabled={dropdownTopic.disabled}
              />

              <DropdownDifficulty difficulty={dropdownDifficulty.difficulty} onChange={dropdownDifficulty.onChange} />

              <DropdownQuestionStatus
                status={dropdownQuestionStatus.status}
                onChange={dropdownQuestionStatus.onChange}
                allowAll={dropdownQuestionStatus.allowAll}
                availableStatuses={dropdownQuestionStatus.availableStatuses}
                disabled={dropdownQuestionStatus.disabled}
                fullWidth={dropdownQuestionStatus.fullWidth}
                minWidth={dropdownQuestionStatus.minWidth}
              />

              {children}
            </>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
